import {
  Button,
  Callout,
  Card,
  FormGroup,
  InputGroup,
} from "@blueprintjs/core";
import { ItemSelect } from "@package/ui-apollo-components";
import {
  type GetLocationListNameIdQuery,
  type GetLocationListNameIdQueryVariables,
  type GetProfileListNameIdQuery,
  type GetProfileListNameIdQueryVariables,
  type PostQrCodeByProfile,
  GetLocationListNameIdDocument,
  GetProfileListNameIdDocument,
} from "@package/ui-gql/index-data-access";
import { produce } from "immer";
import { useMemo } from "react";

type PostAdvancedQrCodeListEditorEntryProps = {
  disabled: boolean;
  code: PostQrCodeByProfile;
  onCodeRemoved: () => void;
  onCodeUpdated: (tile: PostQrCodeByProfile) => void;
};

function PostAdvancedQrCodeListEditorEntry({
  disabled = false,
  code,
  onCodeRemoved,
  onCodeUpdated,
}: PostAdvancedQrCodeListEditorEntryProps) {
  return (
    <Card compact>
      <FormGroup label="Location" disabled={disabled}>
        <ItemSelect<
          GetLocationListNameIdQuery,
          GetLocationListNameIdQueryVariables
        >
          activeItemId={code.location}
          disabled={disabled}
          document={GetLocationListNameIdDocument}
          nullable
          nullableText="Select a Location"
          onItemSelected={(id) =>
            onCodeUpdated(
              produce(code, (draft) => {
                draft.location = id;
              }),
            )
          }
        />
      </FormGroup>
      <FormGroup label="Canvas" disabled={disabled}>
        <ItemSelect<
          GetProfileListNameIdQuery,
          GetProfileListNameIdQueryVariables
        >
          activeItemId={code.profile}
          disabled={disabled}
          document={GetProfileListNameIdDocument}
          nullable
          nullableText="Select a Canvas"
          onItemSelected={(id) =>
            onCodeUpdated(
              produce(code, (draft) => {
                draft.profile = id;
              }),
            )
          }
        />
      </FormGroup>
      <FormGroup label="QR Code Link" disabled={disabled}>
        <InputGroup
          disabled={disabled}
          placeholder="QR Code"
          value={code.qrCodeLink ?? ""}
          onValueChange={(value) =>
            onCodeUpdated(
              produce(code, (draft) => {
                draft.qrCodeLink = value;
              }),
            )
          }
        />
      </FormGroup>
      <div className="flex flex-row justify-end">
        <Button
          disabled={disabled}
          minimal
          intent="danger"
          text="Delete Advanced QR Code"
          onClick={() => onCodeRemoved()}
        />
      </div>
    </Card>
  );
}

type PostAdvancedQrCodeListEditorProps = {
  disabled?: boolean;
  codes?: (PostQrCodeByProfile | null)[];
  onCodeChanged: (codes: PostQrCodeByProfile[]) => void;
};

export function PostAdvancedQrCodeListEditor({
  disabled = false,
  codes,
  onCodeChanged,
}: PostAdvancedQrCodeListEditorProps) {
  const codesValid = useMemo<PostQrCodeByProfile[]>(
    () => codes?.filter((t) => !!t) ?? [],
    [codes],
  );

  return (
    <>
      <Callout
        intent="none"
        title="Advanced QR Codes"
        icon="info-sign"
        className="mb-4"
      >
        <p className="mb-2">
          Advanced QR Codes allow for unique links to be specified for the
          Location and Canvas. If both a Location and Canvas are specified, the
          QR Code will be used only when the Interactive Content is shown on the
          Location in the Canvas. If only Location or Canvas are specified, the
          QR Code will be used when the Interactive Content is shown on the
          Location (for any Canvas), or in the Canvas (on any Location).
        </p>
        <p>
          If no matching Advanced QR Code is found for the Location or Canvas,
          the default &quot;QR Code Link&quot; above will be used.
        </p>
      </Callout>
      {codesValid?.map((code, index) => (
        <div key={index} className="mb-4">
          <PostAdvancedQrCodeListEditorEntry
            disabled={disabled}
            code={code}
            onCodeUpdated={(updated) => {
              onCodeChanged(
                produce(codesValid, (draft) => {
                  draft[index] = updated;
                }),
              );
            }}
            onCodeRemoved={() => {
              onCodeChanged(
                produce(codesValid, (draft) => {
                  draft.splice(index, 1);
                }),
              );
            }}
          />
        </div>
      ))}
      <Button
        icon="add"
        intent="primary"
        minimal
        text="Add Advanced QR Code"
        onClick={() => {
          onCodeChanged(
            produce(codesValid ?? [], (draft) => {
              draft.push({
                qrCodeLink: "",
              });
            }),
          );
        }}
      />
    </>
  );
}
